
import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { useNetwork } from '@/contexts/NetworkContext';
import { Button } from './ui/button';

export const NetworkStatus = () => {
  const { online, connectionInfo, lastError, clearError } = useNetwork();
  const [showBanner, setShowBanner] = useState(false);

  // Show banner when offline or error occurs
  useEffect(() => {
    if (!online || lastError) {
      setShowBanner(true);
    }
  }, [online, lastError]);

  // Hide banner after 10 seconds if back online and no errors
  useEffect(() => {
    let timeoutId: number;
    if (online && !lastError && showBanner) {
      timeoutId = window.setTimeout(() => {
        setShowBanner(false);
      }, 10000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [online, lastError, showBanner]);

  if (!showBanner) return null;

  const handleReconnectClick = () => {
    // Simulate a page refresh to reconnect services
    window.location.reload();
  };

  const handleDismiss = () => {
    clearError();
    setShowBanner(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      {!online && (
        <Alert variant="destructive">
          <WifiOff className="h-4 w-4" />
          <AlertTitle>Network Disconnected</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <p>You appear to be offline. Check your internet connection.</p>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={handleReconnectClick}>
                <RefreshCw className="h-3 w-3 mr-1" /> Reconnect
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                Dismiss
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {online && lastError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Connection Issue</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <p>{lastError}</p>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={handleReconnectClick}>
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                Dismiss
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {online && !lastError && showBanner && (
        <Alert variant="default">
          <Wifi className="h-4 w-4" />
          <AlertTitle>Connection Restored</AlertTitle>
          <AlertDescription className="flex justify-between items-center">
            <p>Your internet connection has been restored.</p>
            <Button size="sm" variant="ghost" onClick={handleDismiss}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default NetworkStatus;
